import { useContext, useEffect, useMemo, useState } from "react";
import { FormBuilderContext } from "../FormBuilderContext";
import { Input } from "../../../BP2Components";
import IntlMessages from "../../../../util/IntlMessages";
import { getListV3 } from "../../../../services/restAPI/ListsV2";
import ListFilterMapper from "../../ListBuilder/QueryBuilder/ListFilterMapper";
import { useListBuilder } from "../../../../context/ListBuilderContext/ListBuilderContext";
import { QueryObject } from "../../ListBuilder/ListBuildertypes";

interface ListColumn {
   techName: string;
   label: string;
}

const ModelFieldEditor = ({ item }) => {
   const { setListItems, listItems } = useContext(FormBuilderContext);
   const { queryObject } = useListBuilder();

   const [labelValue, setLabelValue] = useState(item?.label || "");
   const [listIdValue, setListIdValue] = useState(item.modelField?.list?.listId || "");
   const [listColumns, setListColumns] = useState<ListColumn[]>([]);
   const [initialQuery, setInitialQuery] = useState<QueryObject | undefined>(undefined);

   useEffect(() => {
      setLabelValue(item?.label || "");
      setListIdValue(item.modelField?.list?.listId || "");
      setListColumns([]);
      setInitialQuery(undefined);
   }, [item]);

   useEffect(() => {
      if (!listIdValue || listIdValue.length < 24) {
         setListColumns([]);
         setInitialQuery(undefined);
         return;
      }

      setListColumns([]);
      setInitialQuery(undefined);
      let cancelled = false;

      getListV3(listIdValue, 1, 1)
         .then((res) => {
            if (cancelled) return;
            const columns: ListColumn[] = res?.data?.columns ?? [];
            if (columns.length > 0) {
               const savedFilter =
                  listIdValue === item.modelField?.list?.listId
                     ? item.modelField?.list?.defaultFilter
                     : undefined;
               const normalizedFilter = savedFilter
                  ? Object.fromEntries(
                       Object.entries(savedFilter)
                          .filter(([, value]) => value !== null)
                          .map(([key, value]) => [
                             key,
                             typeof value === "string" ? { techName: value, label: value } : value,
                          ]),
                    )
                  : undefined;
               setInitialQuery(
                  normalizedFilter && Object.keys(normalizedFilter).length > 0
                     ? { type: "", properties: normalizedFilter, subQueries: [], size: 10 }
                     : undefined,
               );
               setListColumns(columns);
            }
         })
         .catch(() => {
            if (cancelled) return;
            setListColumns([]);
            setInitialQuery(undefined);
         });

      return () => {
         cancelled = true;
      };
   }, [listIdValue, item.uuid]);

   useEffect(() => {
      if (listColumns.length === 0) return;

      setListItems((prev) =>
         prev.map((el) => {
            if (el.uuid === item.uuid) {
               return {
                  ...el,
                  modelField: {
                     ...el.modelField,
                     list: {
                        ...el.modelField?.list,
                        defaultFilter: queryObject?.properties ?? {},
                     },
                  },
               };
            }

            if (el.type === "columns" && Array.isArray(el.items)) {
               return {
                  ...el,
                  items: el.items.map((subItem) => {
                     if (subItem?.uuid === item.uuid) {
                        return {
                           ...subItem,
                           modelField: {
                              ...subItem.modelField,
                              list: {
                                 ...subItem.modelField?.list,
                                 defaultFilter: queryObject?.properties ?? {},
                              },
                           },
                        };
                     }
                     return subItem;
                  }),
               };
            }

            return el;
         }),
      );
   }, [queryObject, item.uuid, setListItems]);

   const formFields = useMemo(() => {
      return (listItems ?? [])
         .flatMap((listItem) => {
            if (listItem.uuid === item.uuid) return [];
            if (listItem.type === "columns") {
               return (listItem.items ?? []).filter(
                  (subItem) => subItem && subItem.uuid !== item.uuid,
               );
            }
            if (listItem.type === "section") return [];
            return [listItem];
         })
         .map((field) => ({
            name: field.name ?? field.modelField?.name ?? "",
            label: field.label ?? "",
         }))
         .filter((field) => field.name);
   }, [listItems, item.uuid]);

   const updateItem = (newLabel, newListId) => {
      setListItems((prev) =>
         prev.map((el) => {
            if (el.uuid === item.uuid) {
               const listIdChanged = newListId !== el.modelField?.list?.listId;
               return {
                  ...el,
                  label: newLabel,
                  modelField: {
                     ...el.modelField,
                     list: {
                        ...el.modelField?.list,
                        listId: newListId,
                        ...(listIdChanged && { defaultFilter: {} }),
                     },
                  },
               };
            }

            if (el.type === "columns" && Array.isArray(el.items)) {
               return {
                  ...el,
                  items: el.items.map((subItem) => {
                     if (subItem?.uuid === item.uuid) {
                        const listIdChanged = newListId !== subItem.modelField?.list?.listId;
                        return {
                           ...subItem,
                           label: newLabel,
                           modelField: {
                              ...subItem.modelField,
                              list: {
                                 ...subItem.modelField?.list,
                                 listId: newListId,
                                 ...(listIdChanged && { defaultFilter: {} }),
                              },
                           },
                        };
                     }
                     return subItem;
                  }),
               };
            }

            return el;
         }),
      );
   };

   return (
      <div className="input-conatiner" style={{ marginTop: 16 }}>
         <div>
            <span className="label">hId:</span>
            <Input
               type="text"
               value={listIdValue}
               onChange={(e) => {
                  setListIdValue(e.target.value);
               }}
               onBlur={() => {
                  updateItem(labelValue, listIdValue);
               }}
            />
         </div>

         {listColumns.length > 0 && (
            <div style={{ marginTop: 16 }}>
               <ListFilterMapper
                  listColumns={listColumns}
                  formFields={formFields}
                  initialQuery={initialQuery}
               />
            </div>
         )}
      </div>
   );
};

export default ModelFieldEditor;
